import { Box, Stack, Typography, useTheme } from "@mui/material";
import {
  BellIcon,
  Button,
  CustomEmail,
  GoogleIcon,
  InfoFilledIcon,
  InfoIcon,
  MicrosoftIcon,
  OpenExternalLinkIcon,
  Tooltip,
} from "components";
import { useEmailOauthSignIn } from "controller";
import toast from "react-hot-toast";
import { addMixpanelEvent, isEmailValid } from "utilz/helpers";
import CustomMailServerComponent from "./custom-server";
import { forwardRef, useImperativeHandle, useRef, useState } from "react";
import { SharedMailboxSection } from "./SharedMailboxSection";

export const ServerCards = forwardRef(
  (
    {
      onClick,
      value,
      email,
      isAuthVerified,
      isDisabled,
      onChangeClick,
      isOnboarding = false,
      emailConfigData,
    }: {
      value: any;
      isAuthVerified: boolean;
      email: string;
      isDisabled?: boolean;
      onClick: (value: string) => void;
      onChangeClick?: () => void;
      isOnboarding?: boolean;
      emailConfigData?: any;
    },
    ref,
  ) => {
    const customMailServerRef = useRef<any>(null);
    const { mutateAsync: emailOauthSignInFn } = useEmailOauthSignIn();
    const [validationError, setValidationError] = useState<string | null>(null);
    const [sharedMailboxData, setSharedMailboxData] = useState<{
      isSharedMailBox: boolean;
      sharedMailboxEmail: string | null;
    }>({
      isSharedMailBox: false,
      sharedMailboxEmail: null,
    });

    const validateSharedMailbox = () => {
      if (
        sharedMailboxData.isSharedMailBox &&
        !sharedMailboxData.sharedMailboxEmail
      ) {
        setValidationError("Please enter shared mailbox email");
        return false;
      }
      if (
        sharedMailboxData.isSharedMailBox &&
        sharedMailboxData.sharedMailboxEmail
      ) {
        const isValid = isEmailValid(sharedMailboxData.sharedMailboxEmail);
        if (!isValid) {
          setValidationError("Please enter valid shared mailbox email");
          return false;
        }
      }
      return true;
    };
    const onSignInOAuth = async (provider: any) => {
      try {
        const isValid = validateSharedMailbox();
        if (!isValid) {
          return;
        }

        setValidationError(null);
        const result = await emailOauthSignInFn({
          provider,
          redirect: "control_center",
          sharedMailboxEmail: sharedMailboxData.sharedMailboxEmail || undefined,
        });
        if (result) {
          window.open(result, "_self");
        }
      } catch (e) {
        toast.error(
          "Unable to complete action at the moment. Please try again later.",
        );
      }
    };

    const theme = useTheme();
    const submit = async () => {
      if (value === "custom") {
        const result = await customMailServerRef.current?.submit();
        return result;
      }
    };

    useImperativeHandle(ref, () => ({
      submit,
    }));
    return (
      <>
        <Stack direction={"row"} gap="24px">
          {Object.entries(OwnMailServers).map(([key, item]) => {
            const isSelected = value === item.value;
            return (
              <Stack
                key={key}
                onClick={(e) => {
                  if (!item?.is_disabled && !isDisabled) {
                    onClick(key);
                  }
                }}
                position="relative"
                sx={{
                  aspectRatio: "1/1",
                  flex: 1,
                  borderRadius: "8px",
                  minWidth: "110px",
                  border: isSelected
                    ? `1px solid ${theme.palette.primary.main}`
                    : "1px solid #EDEDED",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "16px",
                  cursor: "pointer",
                  boxShadow: isSelected
                    ? "0px 0px 8px 0px rgba(0, 0, 0, 0.16)"
                    : "0px 0px 4px 0px #00000029",
                  backgroundColor: isSelected ? "#E7E9FE" : "transparent",
                  ...((item?.is_disabled ||
                    (isDisabled && value !== item.value)) && {
                    filter: "grayscale(1)",
                    cursor: "default",
                  }),
                  ...(isSelected && {
                    "&:hover": item?.is_disabled
                      ? {}
                      : {
                          cursor: "pointer",
                          boxShadow: "none",
                          border: `1px solid ${theme.palette.neutral["020"]}`,
                          backgroundColor: theme.palette.neutral["005"],
                        },
                  }),
                }}
              >
                {item.icon}
                <Typography className="text-center" variant="body-medium">
                  {item.name}
                </Typography>
              </Stack>
            );
          })}
        </Stack>
        {value === "custom" ? (
          <CustomMailServerComponent
            onSelectAssigneeClicked={() => {}}
            ref={customMailServerRef}
            emailConfigData={emailConfigData}
          />
        ) : isAuthVerified ? (
          // after authentication view
          <Stack gap={"12px"}>
            {/* icon div  */}
            <Stack gap={"12px"}>
              <Typography
                variant="body-medium"
                color={theme.palette.lightBg.low}
              >
                {`The ${
                  OwnMailServers[value]?.showName || ""
                } account you are currently signed in with is: `}
              </Typography>
            </Stack>
            <Stack
              direction={"row"}
              sx={{ width: "100%", justifyContent: "space-between" }}
            >
              <Stack
                direction={"row"}
                gap={"8px"}
                sx={{
                  alignItems: "center",
                  border: ` ${theme.palette.neutral["020"]}`,
                  padding: "8px",
                  width: "fit-content",
                  backgroundColor: "#EDEDED",
                  borderRadius: "8px",
                }}
              >
                {OwnMailServers[value]?.buttonIcon}
                <Typography
                  variant="body-medium"
                  color={theme.palette.lightBg.low}
                >
                  {email}
                </Typography>
              </Stack>
              {/* <Button
                variant="ghost"
                sx={{
                  p: "2px 8px",
                  m: "0px",
                  minWidth: "fit-content",
                  minHeight: "fit-content",
                  cursor: "pointer",
                }}
                onClick={onChangeClick}
              >
                Change account
              </Button> */}
            </Stack>
          </Stack>
        ) : (
          <Stack gap={"12px"} mt="12px">
            <Typography variant="body-medium" color={theme.palette.lightBg.low}>
              {value === "outlook" ? (
                <>
                  Connect your Office 365 mailbox to DeskDay using Microsoft
                  Graph. Every email sent to this mailbox will be created and
                  managed as a ticket inside DeskDay.
                </>
              ) : (
                <>
                  Connect your Google mailbox to DeskDay. All emails sent to
                  this mailbox will be automatically created and managed as
                  tickets.
                </>
              )}
            </Typography>
            {value === "outlook" && (
              <Stack
                sx={{
                  width: "100%",
                  backgroundColor: theme.palette.primitive.system.blue[25],
                  border: `1px solid ${theme.palette.primitive.system.blue[800]}`,
                  borderRadius: "8px",
                  p: "12px 16px",
                  gap: "8px",
                }}
              >
                <Stack direction={"row"} gap="8px" alignItems={"center"}>
                  <InfoIcon
                    fontSize="small"
                    htmlColor={theme.palette.primitive.system.blue[800]}
                  />
                  <Typography
                    variant="subtitle1"
                    color={theme.palette.primitive.system.blue[800]}
                  >
                    Admin consent required
                  </Typography>
                </Stack>
                <Typography
                  variant="body-small"
                  color={theme.palette.lightBg.low}
                >
                  DeskDay requires a one-time Microsoft Entra admin approval
                  before any mailboxes can be connected. Sign in with an admin
                  account to grant access and continue adding mailboxes.
                </Typography>
                <Box>
                  <Button
                    onClick={() => {
                      window.open(
                        `https://login.microsoftonline.com/common/adminconsent?client_id=${
                          import.meta.env.VITE_APP_OUTLOOK_CONFIG_ID
                        }&state=${
                          import.meta.env.VITE_APP_OUTLOOK_CONFIG_STATE
                        }&redirect_uri=https://${
                          window.location.hostname
                        }/app/control-center/channels/email`,
                        "_blank",
                      );
                    }}
                    isLink
                    variant="ghost"
                    sx={{ textAlign: "left", textDecoration: "underline" }}
                  >
                    Approve for tenant{" "}
                    <OpenExternalLinkIcon
                      fontSize="small"
                      sx={{ width: "18px", height: "18px" }}
                    />
                  </Button>
                </Box>
              </Stack>
            )}
            {value === "outlook" && (
              <SharedMailboxSection
                sharedMailboxData={sharedMailboxData}
                setSharedMailboxData={setSharedMailboxData}
                error={validationError}
                buttonComponent={
                  <SigninButtonComponent
                    value={value}
                    onClick={() => {
                      onSignInOAuth(OwnMailServers[value]?.value);
                    }}
                  />
                }
              />
            )}
            {sharedMailboxData.isSharedMailBox ? null : (
              <SigninButtonComponent
                value={value}
                onClick={() => {
                  onSignInOAuth(OwnMailServers[value]?.value);
                }}
              />
            )}

            {/* <Button
              // startIcon={<GoogleIcon sx={{ width: "16px", height: "16px" }} />}
              startIcon={OwnMailServers[value]?.buttonIcon}
              variant="secondary"
              sx={{
                width: "fit-content",
              }}
              onClick={() => {
                onSignInOAuth(OwnMailServers[value]?.value);
              }}
            >
              {OwnMailServers[value]?.buttonText}
            </Button> */}
            {value === "gmail" && (
              <Stack
                mt="12px"
                p="16px"
                borderRadius={"8px"}
                gap="8px"
                bgcolor={"warning-minor.tint"}
              >
                <Stack direction="row" alignItems="center" gap="8px">
                  <InfoFilledIcon color="warning" />
                  <Typography variant="h6">Warning:</Typography>
                </Stack>
                <Typography variant="body-medium" color="lightBg.low">
                  Please ensure you{" "}
                  <span
                    style={{
                      color: theme.palette.lightBg.main,
                    }}
                  >
                    enable the checkbox
                  </span>{" "}
                  to grant DeskDay.ai access to your Google Account data for the
                  custom mailbox to work properly, before clicking 'Continue' on
                  the authentication page.
                </Typography>
              </Stack>
            )}
          </Stack>
        )}
      </>
    );
  },
);

const SigninButtonComponent = ({
  value,
  onClick,
}: {
  value: string;
  onClick: () => void;
}) => {
  return (
    <Button
      // startIcon={<GoogleIcon sx={{ width: "16px", height: "16px" }} />}
      startIcon={OwnMailServers[value]?.buttonIcon}
      variant="secondary"
      sx={{
        width: "fit-content",
        // textTransform: "none",
      }}
      onClick={onClick}
    >
      <>{OwnMailServers[value]?.buttonText}</>
    </Button>
  );
};

const OwnMailServers: Record<string, any> = {
  outlook: {
    name: "Office 365",
    icon: <MicrosoftIcon sx={{ width: "48px", height: "48px" }} />,
    buttonIcon: <MicrosoftIcon sx={{ width: "16px", height: "16px" }} />,
    buttonText: "Sign in with Microsoft",
    value: "outlook",
    showName: "Microsoft",
  },
  gmail: {
    name: "Google",
    icon: <GoogleIcon sx={{ width: "48px", height: "48px" }} />,
    buttonIcon: <GoogleIcon sx={{ width: "16px", height: "16px" }} />,
    buttonText: "Sign in with Google",
    value: "gmail",
    showName: "Google",
  },
  custom: {
    name: "Custom",
    icon: <CustomEmail sx={{ width: "48px", height: "48px" }} />,
    buttonIcon: <CustomEmail sx={{ width: "16px", height: "16px" }} />,
    buttonText: "",
    value: "custom",
    showName: "Custom",
  },
};
